import { onBeforeUnmount, onMounted, watch, type Ref } from 'vue';
import { storeToRefs } from 'pinia';
import { setShapeId } from 'shared/shapes/domain/shapeGeoJson';
import { useMapStyleStore } from 'shared/mapStyles/stores/mapStyleStore';
import type {
  LogYardContext,
  BulkStorageContext,
  ProductContext,
  ProductGroupContext,
  ShapeContext,
  StorageAreaContext,
  StorageAreaStatuses,
  StorageAreaTypes,
} from 'shared/mapStyles/domain/shapeContext';
import { useRafFn } from '@vueuse/core';
import { StorageAreaType } from 'shared/storageAreas/domain/storageAreaType';
import { calculateTextColor } from 'shared/color';
import { StorageAreaStatus } from 'shared/storageAreas/domain/storageAreaStatus';
import { useLanguageStore } from '../../translations/stores/languageStore';
import { useProductGroupStore } from '../../products/groups/stores/productGroupStore';
import { useProductStore } from '../../products/stores/productStore';
import type { Product } from '../../products/domain/product';
import type { Shape } from '../../shapes/domain/shape';
import { useShapeStore } from '../../shapes/stores/shapeStore';
import { useStorageAreaStore } from '../../storageAreas/stores/storageAreaStore';
import type {
  BulkContent,
  BulkStorageData,
  LogYardContent,
  LogYardData,
  StorageArea,
} from '../../storageAreas/domain/storageArea';
import { useCoilStore } from '../../coils/stores/coilStore';
import type { MapStyle } from '../../mapStyles/domain/mapStyle';
import { useProductionOrderStore } from '../../productionOrders/stores/productionOrderStore';
import OLMap from 'ol/Map';
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import type { Geometry } from 'ol/geom';
import type Feature from 'ol/Feature';
import GeoJSON from 'ol/format/GeoJSON.js';
import { rectangleStyle } from '../styles/rectangleStyle';
import Style from 'ol/style/Style';
import Stroke from 'ol/style/Stroke';
import Fill from 'ol/style/Fill';
import Text from 'ol/style/Text';
import { Color } from '../../common/domain/color';
import { getDaysSinceDate } from '../../common/date';
import { parseRichText } from '../../mapStyles/domain/richText';
import {
  calculateFillLevelVolume,
  calculateFillLevelWeight,
} from '../../storageAreas/composables/useCalculateFillLevel';
import { isDebugEnabled } from 'shared/common/debug';
import { ProductType } from '../../products/domain/productType';
import type { EntityChanged } from '../../common/composables/entityStore';
import { unByKey } from 'ol/Observable';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

/** Enable with the 'shapeLayerPerf' debug flag. Logs a timing table for every render pass. */
const PERF_DEBUG_FLAG = 'shapeLayerPerf';

/**
 * Max time spent rendering view models in one animation frame. When exceeded, the rest of the
 * dirty view models are rendered in the following frames so the UI stays responsive.
 * Set to Infinity to always render everything in a single frame.
 */
const RENDER_BUDGET_MS = 10;

/** How often (in rendered view models) the clock is checked against RENDER_BUDGET_MS. */
const BUDGET_CHECK_INTERVAL = 8;

const DEFAULT_OPACITY = 0.8;
const DIMMED_OPACITY = 0.2;

const storageAreaTypes: StorageAreaTypes = {
  producer: StorageAreaType.Producer,
  consumer: StorageAreaType.Consumer,
  vehicle: StorageAreaType.Vehicle,
  logSorterIntake: StorageAreaType.LogSorterIntake,
  sawLineIntake: StorageAreaType.SawLineIntake,
  logSorterBin: StorageAreaType.LogSorterBin,
  sortedPile: StorageAreaType.SortedPile,
  unsortedPile: StorageAreaType.UnsortedPile,
  truck: StorageAreaType.Truck,
  train: StorageAreaType.Train,
  bulk: StorageAreaType.Bulk,
};

const storageAreaStatuses: StorageAreaStatuses = {
  undefined: StorageAreaStatus.Undefined,
  empty: StorageAreaStatus.Empty,
  inuse: StorageAreaStatus.InUse,
  full: StorageAreaStatus.Full,
};

// ---------------------------------------------------------------------------
// Public types
// ---------------------------------------------------------------------------

export interface ShapeLayerClickEvent {
  shapeId: string | null;
  storageAreaId: string | null;
  multiple: boolean;
}

export type ShapeLayerClickListener = (ev: ShapeLayerClickEvent) => void;

export interface ShapeLayerOptions {
  mapStyle?: Ref<MapStyle>;
}

// ---------------------------------------------------------------------------
// Internal types
// ---------------------------------------------------------------------------

interface ShapeViewModel {
  id: string;

  shape: Shape;

  /** True when the OpenLayers geometry must be (re)created from shape.shapeGeoJson. */
  geometryDirty: boolean;
  deleteScheduled: boolean;

  feature: Feature<Geometry> | null;
}

interface AreaRef {
  id: string;
  name: string;
}

interface CrossAreaIndex {
  /** productId -> ids of areas that hold or allow it (active, not full, not a log sorter bin). */
  holders: Map<string, Set<string>>;
  /** productId -> sawline intakes that allow it. */
  sawIntakesByProduct: Map<string, AreaRef[]>;
  /** productId -> log sorter bins that hold it. */
  binsByProduct: Map<string, AreaRef[]>;
  /** All bins in store order (for stable name output). */
  bins: AreaRef[];
}

type ProductionOrder = ReturnType<typeof useProductionOrderStore>['productionOrders'][number];

/**
 * Lookups that are expensive to build but identical for every shape in a render batch.
 * Created when a batch starts and thrown away when the batch ends (or when the underlying
 * data changes, see invalidateLookups).
 */
interface LookupCache {
  index: CrossAreaIndex | null;
  vehicleProducts: Map<string, Product> | null;
  vehicleKinds: { hasLog: boolean; hasBulk: boolean } | null;
  areaProducts: Map<string, Map<string, Product>>;
  areaAllowedProducts: Map<string, Map<string, Product>>;
  logSorterIntakes: StorageArea[] | null;
  productionOrdersByName: Map<string, ProductionOrder> | null;
  productionOrderProducts: Map<string, Map<string, Product>>;
}

interface FrameChanges {
  /** New features, added to the source in one batch at the end of the frame. */
  additions: Feature<Geometry>[];
  /** True when properties of already added features changed (needs one source.changed()). */
  propertiesChanged: boolean;
}

interface FeatureProperties {
  label: string;
  color: string;
  outlineColor: string;
  outlineSize: number;
  opacity: number;
  fontSize: number;
  textColor: string;
  messageIconVisible: boolean;
  zIndex: number;
}

interface ShapeStyleResult {
  color: string;
  outlineColor: string;
  outlineSize: number;
  label: string;
  opacity: number;
  fontSize: number;
  messageIconVisible: boolean;
}

// ---------------------------------------------------------------------------
// Debug / performance helpers
// ---------------------------------------------------------------------------

class PerfStats {
  private readonly timings = new Map<string, { ms: number; calls: number }>();
  private readonly counters = new Map<string, number>();

  add(label: string, ms: number) {
    const entry = this.timings.get(label);
    if (entry) {
      entry.ms += ms;
      entry.calls++;
    } else {
      this.timings.set(label, { ms, calls: 1 });
    }
  }

  count(label: string, n = 1) {
    this.counters.set(label, (this.counters.get(label) ?? 0) + n);
  }

  report(title: string, totalMs: number) {
    const round = (value: number) => Math.round(value * 100) / 100;

    const rows = [...this.timings.entries()]
      .sort((a, b) => b[1].ms - a[1].ms)
      .map(([section, { ms, calls }]) => ({
        section,
        'total ms': round(ms),
        calls,
        'avg µs': round((ms / calls) * 1000),
      }));

    console.groupCollapsed(`[shapeLayer] ${title}: ${round(totalMs)} ms`);
    console.debug('Sections are inclusive and can be nested, so they do not add up to the total.');
    console.table(rows);
    console.debug('Counters', Object.fromEntries(this.counters));
    console.groupEnd();
  }
}

function tick(perf: PerfStats | null): number {
  return perf ? performance.now() : 0;
}

function tock(perf: PerfStats | null, label: string, startedAt: number) {
  if (perf) perf.add(label, performance.now() - startedAt);
}

// ---------------------------------------------------------------------------
// Small pure helpers
// ---------------------------------------------------------------------------

function lazy<T>(fn: () => T): () => T {
  let done = false;
  let value!: T;
  return () => {
    if (!done) {
      value = fn();
      done = true;
    }
    return value;
  };
}

function byNameNumeric(a: Product, b: Product) {
  return a.name.localeCompare(b.name, undefined, { numeric: true });
}

function hasOther(refs: AreaRef[] | undefined, selfId: string, name?: string): boolean {
  return !!refs?.some((r) => r.id !== selfId && (name === undefined || r.name === name));
}

function createLookupCache(): LookupCache {
  return {
    index: null,
    vehicleProducts: null,
    vehicleKinds: null,
    areaProducts: new Map(),
    areaAllowedProducts: new Map(),
    logSorterIntakes: null,
    productionOrdersByName: null,
    productionOrderProducts: new Map(),
  };
}

/** Sets changed properties silently (no per-feature events). Returns true if anything changed. */
function applyProperties(feature: Feature<Geometry>, props: FeatureProperties): boolean {
  let changed = false;

  for (const key of Object.keys(props) as (keyof FeatureProperties)[]) {
    const value = props[key];
    if (feature.get(key) !== value) {
      feature.set(key, value, true);
      changed = true;
    }
  }

  return changed;
}

// ---------------------------------------------------------------------------
// Composable
// ---------------------------------------------------------------------------

export function useShapeLayer(options?: ShapeLayerOptions) {
  const mapStyleStore = useMapStyleStore();
  const languageStore = useLanguageStore();
  const productGroupStore = useProductGroupStore();
  const productStore = useProductStore();
  const shapeStore = useShapeStore();
  const storageAreaStore = useStorageAreaStore();
  const coilStore = useCoilStore();
  const productionOrderStore = useProductionOrderStore();

  const geojson = new GeoJSON();

  const selectedStyle = getMapStyle();

  // ---- State (must be declared above the return statement) ----------------

  let onClickCallback: ShapeLayerClickListener = (ev) => {};

  const viewModels = new Map<string, ShapeViewModel>();
  /** View models that need to be rendered. Only these are visited by the render loop. */
  const dirtyViewModels = new Set<ShapeViewModel>();

  const hiddenShapeIds = new Set<string>();
  const hiddenStorageAreaIds = new Set<string>();

  let searchActive: boolean = false;
  const searchedStorageAreaIds = new Set<string>();

  const selectedStorageAreaIds = new Set<string>();

  let vehicleLogYardContent: LogYardContent[] | null = null;
  let vehicleBulkContent: BulkContent[] | null = null;

  let insideStorageAreaId: string | null = null;

  // Render batch state. A batch starts when the first view model becomes dirty and ends when
  // no dirty view models are left (a batch can span several frames when time slicing kicks in).
  let lookups: LookupCache = createLookupCache();
  let batchInProgress = false;
  let batchPropertiesChanged = false;
  let framePerf: PerfStats | null = null;
  let fullRenderReason: string | null = null;
  let fullRenderRequestedAt = 0;

  // Tracks whether the active map style reads values that depend on OTHER storage areas.
  // If not, a change to one storage area only needs to re-render that area's shapes.
  let crossAreaUsed = false;
  let crossAreaScanComplete = false;

  // ---- Subscriptions -------------------------------------------------------

  const unsubscribers: Array<() => void> = [];

  onMounted(() => {
    unsubscribers.push(
      shapeStore.changed.on((events) => {
        for (const ev of events) {
          if (ev.action === 'removed') {
            deleteShape(ev.id);
          } else {
            mergeShape(ev.data);
          }
        }
      }),

      productStore.changed.on(() => {
        invalidateLookups();
        scheduleRenderAll('productStore changed');
      }),

      productionOrderStore.changed.on(() => {
        invalidateLookups();
        scheduleRenderAll('productionOrderStore changed');
      }),

      coilStore.changed.on(() => scheduleRenderAll('coilStore changed')),

      storageAreaStore.changed.on((events) => {
        invalidateLookups();

        if (!crossAreaScanComplete || crossAreaUsed) {
          const reason = !crossAreaScanComplete
            ? 'cross-area scan not complete yet'
            : 'map style reads cross-area values';
          scheduleRenderAll(`storageAreaStore changed: ${reason}`);
          return;
        }

        for (const ev of events) {
          if (!ev.id) {
            scheduleRenderAll('storageAreaStore changed: event has no area id');
            return;
          }
          scheduleRenderStorageArea(ev.id);
        }
      }),
    );

    for (const shape of shapeStore.shapes) {
      mergeShape(shape);
    }
  });

  onBeforeUnmount(() => {
    for (const unsubscribe of unsubscribers) unsubscribe();
    unsubscribers.length = 0;
  });

  return {
    attach,
    hideShapes,
    hideStorageArea,
    addClickListener,
    selectStorageArea,
    deselectStorageArea,
    setVehicleLogYardContent,
    setVehicleBulkContent,
    insideStorageArea,
    searchStorageAreas,
    clearSearchStorageAreas,
  };

  // =========================================================================
  // Public API
  // =========================================================================

  function getMapStyle() {
    if (options && options.mapStyle) return options.mapStyle;
    const { selectedStyle } = storeToRefs(mapStyleStore);
    return selectedStyle;
  }

  function getShapeStyle() {
    return selectedStyle.value?.shape;
  }

  function addClickListener(listener: ShapeLayerClickListener) {
    onClickCallback = listener;
  }

  function setVehicleLogYardContent(logYardContent: LogYardContent[] | null) {
    if (sameContent(vehicleLogYardContent, logYardContent)) return;

    vehicleLogYardContent = logYardContent;
    invalidateLookups();
    scheduleRenderAll('vehicle log yard content changed');
  }

  function setVehicleBulkContent(bulkContent: BulkContent[] | null) {
    if (sameContent(vehicleBulkContent, bulkContent)) return;

    vehicleBulkContent = bulkContent;
    invalidateLookups();
    scheduleRenderAll('vehicle bulk content changed');
  }

  function insideStorageArea(storageAreaId: string | null) {
    if (storageAreaId === insideStorageAreaId) return;
    if (insideStorageAreaId !== null) {
      scheduleRenderStorageArea(insideStorageAreaId);
    }

    insideStorageAreaId = storageAreaId;

    if (storageAreaId !== null) {
      scheduleRenderStorageArea(storageAreaId);
    }
  }

  function selectStorageArea(id: string) {
    if (selectedStorageAreaIds.size === 1 && selectedStorageAreaIds.has(id)) return;
    scheduleRenderStorageAreas(selectedStorageAreaIds);

    selectedStorageAreaIds.clear();
    selectedStorageAreaIds.add(id);

    scheduleRenderStorageAreas(selectedStorageAreaIds);
  }

  function deselectStorageArea() {
    scheduleRenderStorageAreas(selectedStorageAreaIds);
    selectedStorageAreaIds.clear();
  }

  function hideStorageArea(action: (ids: Set<string>) => void) {
    scheduleRenderStorageAreas(hiddenStorageAreaIds);
    action(hiddenStorageAreaIds);
    scheduleRenderStorageAreas(hiddenStorageAreaIds);
  }

  function hideShapes(action: (ids: Set<string>) => void) {
    scheduleRenderShapes(hiddenShapeIds);
    action(hiddenShapeIds);
    scheduleRenderShapes(hiddenShapeIds);
  }

  function searchStorageAreas(action: (ids: Set<string>) => void) {
    searchActive = true;
    action(searchedStorageAreaIds);
    scheduleRenderAll('storage area search changed');
  }

  function clearSearchStorageAreas() {
    searchActive = false;
    searchedStorageAreaIds.clear();
    scheduleRenderAll('storage area search cleared');
  }

  // =========================================================================
  // Scheduling
  // =========================================================================

  function markDirty(viewModel: ShapeViewModel) {
    dirtyViewModels.add(viewModel);
  }

  function scheduleRenderAll(reason: string) {
    fullRenderReason ??= reason;
    fullRenderRequestedAt = performance.now();
  }

  function markAllDirty(reason: string) {
    for (const viewModel of viewModels.values()) {
      markDirty(viewModel);
    }

    if (isDebugEnabled(PERF_DEBUG_FLAG)) {
      console.debug(`[shapeLayer] Full re-render (${dirtyViewModels.size} view models): ${reason}`);
    }
  }

  function scheduleRenderShape(id: string) {
    const viewModel = viewModels.get(id);
    if (!viewModel) return;
    markDirty(viewModel);
  }

  function scheduleRenderShapes(ids: Set<string>) {
    for (const id of ids) {
      scheduleRenderShape(id);
    }
  }

  function scheduleRenderStorageArea(id: string) {
    const shapes = shapeStore.getByStorageAreaId(id);
    if (!shapes) return;
    for (const shape of shapes) {
      const viewModel = viewModels.get(shape.id);
      if (!viewModel) continue;
      markDirty(viewModel);
    }
  }

  function scheduleRenderStorageAreas(ids: Set<string>) {
    for (const id of ids) {
      scheduleRenderStorageArea(id);
    }
  }

  function mergeShape(shape: Shape) {
    let viewModel = viewModels.get(shape.id);

    if (viewModel) {
      viewModel.shape = shape;
    } else {
      viewModel = {
        id: shape.id,
        deleteScheduled: false,
        shape: shape,
        feature: null,
        geometryDirty: true,
      };
      viewModels.set(viewModel.id, viewModel);
    }

    viewModel.geometryDirty = true;
    markDirty(viewModel);
  }

  function deleteShape(id: string) {
    const viewModel = viewModels.get(id);
    if (!viewModel) return;
    viewModel.deleteScheduled = true;
    markDirty(viewModel);
  }

  /** Drops cached lookups so the next shape rendered sees fresh store data. */
  function invalidateLookups() {
    lookups = createLookupCache();
  }

  // =========================================================================
  // Lazy / timed helpers (timings are only collected when debug is enabled)
  // =========================================================================

  function lazyTimed<T>(label: string, fn: () => T): () => T {
    const perf = framePerf;
    if (!perf) return lazy(fn);

    return lazy(() => {
      const t0 = performance.now();
      try {
        return fn();
      } finally {
        perf.add(label, performance.now() - t0);
      }
    });
  }

  /** Lazy value that depends on other storage areas. Reading it flags the style as cross-area. */
  function lazyCrossArea<T>(label: string, fn: () => T): () => T {
    return lazyTimed(label, () => {
      crossAreaUsed = true;
      return fn();
    });
  }

  function crossAreaFunc<A extends unknown[], R>(
    label: string,
    fn: (...args: A) => R,
  ): (...args: A) => R {
    const perf = framePerf;

    return (...args: A) => {
      crossAreaUsed = true;
      if (!perf) return fn(...args);

      const t0 = performance.now();
      try {
        return fn(...args);
      } finally {
        perf.add(label, performance.now() - t0);
      }
    };
  }

  function sameContent<T>(a: T[] | null, b: T[] | null): boolean {
    const aEmpty = !a || a.length === 0;
    const bEmpty = !b || b.length === 0;
    if (aEmpty || bEmpty) return aEmpty && bEmpty;
    return JSON.stringify(a) === JSON.stringify(b);
  }

  // =========================================================================
  // Product lookups (raw, uncached)
  // =========================================================================

  function getLogYardProducts(contents: LogYardContent[]) {
    const products = new Map<string, Product>();

    for (const content of contents) {
      const product = productStore.getById(content.productId);
      if (!product) continue;
      products.set(product.id, product);
    }

    return products;
  }

  function getBulkProducts(contents: BulkContent[]) {
    const products = new Map<string, Product>();

    for (const content of contents) {
      const product = productStore.getById(content.productId);
      if (!product) continue;
      products.set(product.id, product);
    }

    return products;
  }

  function getAllowedProducts(productIds: string[]) {
    const products = new Map<string, Product>();

    for (const productId of productIds) {
      const product = productStore.getById(productId);
      if (!product) continue;
      products.set(product.id, product);
    }

    return products;
  }

  function getStorageAreaProducts(storageArea: StorageArea): Map<string, Product> {
    const products = new Map<string, Product>();

    if (storageArea.logYardData) {
      for (const [key, product] of getLogYardProducts(storageArea.logYardData.content)) {
        products.set(key, product);
      }
    }

    if (storageArea.bulkStorageData) {
      for (const [key, product] of getBulkProducts(storageArea.bulkStorageData.content)) {
        products.set(key, product);
      }
    }

    return products;
  }

  function getStorageAreaAllowedProducts(storageArea: StorageArea) {
    if (storageArea.logYardData) {
      return getAllowedProducts(storageArea.logYardData.allowedProducts);
    } else if (storageArea.bulkStorageData) {
      return getAllowedProducts(storageArea.bulkStorageData.allowedProducts);
    } else {
      return new Map<string, Product>();
    }
  }

  function sortedProductNames(productIds: string[]): string {
    const products = [...getAllowedProducts(productIds).values()];
    products.sort(byNameNumeric);
    return products.map((p) => p.name).join(', ');
  }

  // =========================================================================
  // Cached lookups (valid for one render batch)
  // =========================================================================

  function getAreaProducts(storageArea: StorageArea): Map<string, Product> {
    let products = lookups.areaProducts.get(storageArea.id);
    if (!products) {
      products = getStorageAreaProducts(storageArea);
      lookups.areaProducts.set(storageArea.id, products);
    }
    return products;
  }

  function getAreaAllowedProducts(storageArea: StorageArea): Map<string, Product> {
    let products = lookups.areaAllowedProducts.get(storageArea.id);
    if (!products) {
      products = getStorageAreaAllowedProducts(storageArea);
      lookups.areaAllowedProducts.set(storageArea.id, products);
    }
    return products;
  }

  function getVehicleProducts(): Map<string, Product> {
    if (lookups.vehicleProducts) return lookups.vehicleProducts;

    const t0 = tick(framePerf);
    const products = new Map<string, Product>();

    if (vehicleLogYardContent) {
      for (const [key, product] of getLogYardProducts(vehicleLogYardContent)) {
        products.set(key, product);
      }
    }

    if (vehicleBulkContent) {
      for (const [key, product] of getBulkProducts(vehicleBulkContent)) {
        products.set(key, product);
      }
    }

    lookups.vehicleProducts = products;
    tock(framePerf, 'lookup: vehicle products', t0);
    return products;
  }

  function getVehicleKinds() {
    if (lookups.vehicleKinds) return lookups.vehicleKinds;

    let hasLog = false;
    let hasBulk = false;

    for (const product of getVehicleProducts().values()) {
      if (product.type === ProductType.Log) hasLog = true;
      if (product.type === ProductType.Bulk) hasBulk = true;
    }

    lookups.vehicleKinds = { hasLog, hasBulk };
    return lookups.vehicleKinds;
  }

  function getIndex(): CrossAreaIndex {
    if (!lookups.index) {
      const t0 = tick(framePerf);
      lookups.index = buildIndex();
      tock(framePerf, 'lookup: build cross-area index', t0);
    }
    return lookups.index;
  }

  function buildIndex(): CrossAreaIndex {
    const holders = new Map<string, Set<string>>();
    const sawIntakesByProduct = new Map<string, AreaRef[]>();
    const binsByProduct = new Map<string, AreaRef[]>();
    const bins: AreaRef[] = [];

    const addRef = (map: Map<string, AreaRef[]>, productId: string, ref: AreaRef) => {
      const list = map.get(productId);
      if (list) list.push(ref);
      else map.set(productId, [ref]);
    };

    const addHolder = (productId: string, areaId: string) => {
      const set = holders.get(productId);
      if (set) set.add(areaId);
      else holders.set(productId, new Set([areaId]));
    };

    for (const area of storageAreaStore.storageAreas) {
      if (
        area.status === StorageAreaStatus.Full ||
        area.type === StorageAreaType.LogSorterBin ||
        !area.active
      ) {
        continue;
      }
      for (const id of getAreaProducts(area).keys()) addHolder(id, area.id);
      for (const id of getAreaAllowedProducts(area).keys()) addHolder(id, area.id);
    }

    for (const intake of storageAreaStore.sawLineIntakes) {
      const ref = { id: intake.id, name: intake.name };
      for (const id of getAreaAllowedProducts(intake).keys()) {
        addRef(sawIntakesByProduct, id, ref);
      }
    }

    for (const bin of storageAreaStore.logSorterBins) {
      const ref = { id: bin.id, name: bin.name };
      bins.push(ref);
      for (const id of getAreaProducts(bin).keys()) addRef(binsByProduct, id, ref);
    }

    return { holders, sawIntakesByProduct, binsByProduct, bins };
  }

  /** Products produced by the production orders of the log sorter intakes (cached per sorter name). */
  function getProductionOrderProducts(logSorterName: string): Map<string, Product> {
    const key = logSorterName || '';
    const cached = lookups.productionOrderProducts.get(key);
    if (cached) return cached;

    const t0 = tick(framePerf);

    lookups.logSorterIntakes ??= storageAreaStore.storageAreas.filter(
      (s) => s.type === StorageAreaType.LogSorterIntake,
    );

    if (!lookups.productionOrdersByName) {
      const byName = new Map<string, ProductionOrder>();
      for (const productionOrder of productionOrderStore.productionOrders) {
        // Keep the first order per name, same as the previous filter(...)[0] behavior.
        if (!byName.has(productionOrder.name)) byName.set(productionOrder.name, productionOrder);
      }
      lookups.productionOrdersByName = byName;
    }

    const products = new Map<string, Product>();

    for (const intake of lookups.logSorterIntakes) {
      if (key && intake.name !== key) continue;
      if (!intake.orderName) continue;

      const productionOrder = lookups.productionOrdersByName.get(intake.orderName);
      if (!productionOrder) continue;

      for (const recipe of productionOrder.recipes) {
        for (const outProduct of recipe.outProducts) {
          const product = productStore.getById(outProduct.productId);
          if (product) products.set(product.id, product);
        }
      }
    }

    lookups.productionOrderProducts.set(key, products);
    tock(framePerf, 'lookup: production order products', t0);
    return products;
  }

  // =========================================================================
  // Storage area comparisons (used lazily by the map style)
  // =========================================================================

  function sameProductsAsVehicle(storageArea: StorageArea) {
    const vehicleProducts = getVehicleProducts();

    if (vehicleProducts.size === 0) {
      return false;
    }

    if (storageArea.status === StorageAreaStatus.Full) {
      // If the storage area is full then it should never be suggested as having the same product as the vehicle
      // even if it has the same products.
      return false;
    }

    const storageAreaProducts = getAreaProducts(storageArea);

    for (const vehicleProduct of vehicleProducts.values()) {
      if (storageAreaProducts.has(vehicleProduct.id)) {
        return true;
      }
    }

    return false;
  }

  function sameAllowedProductsAsVehicle(storageArea: StorageArea) {
    const vehicleProducts = getVehicleProducts();

    if (vehicleProducts.size === 0) {
      return false;
    }

    if (storageArea.status === StorageAreaStatus.Full) {
      // If the storage area is full then it should never be suggested as having the same product as the vehicle
      // even if it has the same products.
      return false;
    }

    const storageAreaAllowedProducts = getAreaAllowedProducts(storageArea);
    const { hasLog, hasBulk } = getVehicleKinds();

    // If the storage area is a pile or bulk and has no allowed products but multiple products are allowed,
    // it should be considered as matching any vehicle products.
    if (
      storageAreaAllowedProducts.size === 0 &&
      ((storageArea.logYardData?.allowMultipleProducts === true && hasLog) ||
        (storageArea.bulkStorageData?.allowMultipleProducts === true && hasBulk))
    ) {
      return true;
    }

    for (const vehicleProduct of vehicleProducts.values()) {
      if (storageAreaAllowedProducts.has(vehicleProduct.id)) {
        return true;
      }
    }

    return false;
  }

  function sameProductAsAllSawLineIntake(storageArea: StorageArea): boolean {
    const { sawIntakesByProduct } = getIndex();
    for (const productId of getAreaProducts(storageArea).keys()) {
      if (hasOther(sawIntakesByProduct.get(productId), storageArea.id)) return true;
    }
    return false;
  }

  function sameProductAsSawLineIntake(storageArea: StorageArea) {
    return (sawlineName: string): boolean => {
      const { sawIntakesByProduct } = getIndex();
      for (const productId of getAreaProducts(storageArea).keys()) {
        if (hasOther(sawIntakesByProduct.get(productId), storageArea.id, sawlineName)) return true;
      }
      return false;
    };
  }

  function sameProductAsLogSorterBin(storageArea: StorageArea): boolean {
    if (storageArea.type === StorageAreaType.LogSorterBin) return false;

    const { binsByProduct } = getIndex();
    for (const productId of getAreaProducts(storageArea).keys()) {
      if (hasOther(binsByProduct.get(productId), storageArea.id)) return true;
    }
    return false;
  }

  function productExistsInOtherStorageAreas(storageArea: StorageArea): boolean {
    const mine = getAreaProducts(storageArea);
    if (mine.size === 0) return true;

    const { holders } = getIndex();
    for (const productId of mine.keys()) {
      const set = holders.get(productId);
      // "other" = any holder that isn't this area
      if (set && set.size - (set.has(storageArea.id) ? 1 : 0) > 0) return true;
    }
    return false;
  }

  function getLogSorterBinForProducts(storageArea: StorageArea): string | null {
    const { binsByProduct, bins } = getIndex();
    const matched = new Set<string>();

    for (const productId of getAreaProducts(storageArea).keys()) {
      for (const ref of binsByProduct.get(productId) ?? []) matched.add(ref.id);
    }

    if (matched.size === 0) return null;

    return bins
      .filter((b) => matched.has(b.id))
      .map((b) => b.name)
      .join(', ');
  }

  function sameProductAsLogSorterProductionOrder(storageArea: StorageArea) {
    return (logSorterName: string): boolean => {
      const orderProducts = getProductionOrderProducts(logSorterName);
      if (orderProducts.size === 0) return false;

      // Fetch fresh storage area data
      const currentStorageArea = storageAreaStore.getById(storageArea.id);
      if (!currentStorageArea) return false;

      for (const productId of getAreaProducts(currentStorageArea).keys()) {
        if (orderProducts.has(productId)) return true;
      }

      for (const productId of getAreaAllowedProducts(currentStorageArea).keys()) {
        if (orderProducts.has(productId)) return true;
      }

      return false;
    };
  }

  function allowedToPlaceVehicleContentInLogYard(logYard: LogYardData) {
    if (!vehicleLogYardContent || vehicleLogYardContent.length === 0) return false;

    const allowedProducts = logYard.allowedProducts;

    for (const content of vehicleLogYardContent) {
      if (!allowedProducts.some((productId) => content.productId === productId)) {
        return false;
      }
    }

    return true;
  }

  function allowedToPlaceVehicleContentInBulkStorage(bulkStorage: BulkStorageData) {
    if (!vehicleBulkContent || vehicleBulkContent.length === 0) return false;

    const allowedProducts = bulkStorage.allowedProducts;

    for (const content of vehicleBulkContent) {
      if (!allowedProducts.some((productId) => content.productId === productId)) {
        return false;
      }
    }

    return true;
  }

  // =========================================================================
  // Context building
  // =========================================================================

  /**
   * Builds the context the map style expressions are evaluated against.
   *
   * Expensive values (cross-area lookups, date formatting, product name strings) are exposed as
   * getters so they are only computed when the active map style actually reads them.
   * Do not spread, clone or serialize the context, that would invoke every getter.
   */
  function buildShapeContext(
    shape: Shape,
    storageArea: StorageArea | undefined,
    selected: boolean,
  ): ShapeContext {
    let productContext: ProductContext | null = null;
    let storageAreaContext: StorageAreaContext | null = null;
    let logYardContext: LogYardContext | null = null;
    let bulkStorageContext: BulkStorageContext | null = null;
    let productGroupContext: ProductGroupContext | null = null;

    if (storageArea) {
      const logYard = storageArea.logYardData;
      const bulk = storageArea.bulkStorageData;

      // ---- Products in the storage area ----
      const productIds = new Set<string>();
      let allowedToPlace = false;

      if (logYard) {
        allowedToPlace = allowedToPlaceVehicleContentInLogYard(logYard);

        for (const content of logYard.content) {
          productIds.add(content.productId);
        }
      }

      for (const coil of coilStore.getByStorageAreaId(storageArea.id)) {
        productIds.add(coil.productId);
      }

      if (bulk) {
        allowedToPlace = allowedToPlaceVehicleContentInBulkStorage(bulk);

        for (const content of bulk.content) {
          productIds.add(content.productId);
        }
      }

      const products: Product[] = [];

      for (const productId of productIds) {
        const product = productStore.getById(productId);
        if (!product) continue;
        products.push(product);
      }

      products.sort(byNameNumeric);

      const productNames = lazy(() => products.map((p) => p.name).join(', '));

      // The log yard rules win over the bulk rules when both exist (same as before).
      const nameSource = logYard ?? bulk;
      const displayName = lazy(() => {
        if (!nameSource) return '';
        return nameSource.disableAutomaticProductChange
          ? sortedProductNames(nameSource.allowedProducts)
          : productNames();
      });

      // ---- Storage area context ----
      const sameAsVehicle = lazyTimed('lazy: sameProductsAsVehicle', () =>
        sameProductsAsVehicle(storageArea),
      );
      const sameAllowedAsVehicle = lazyTimed('lazy: sameAllowedProductsAsVehicle', () =>
        sameAllowedProductsAsVehicle(storageArea),
      );
      const sameSawAll = lazyCrossArea('lazy: sameProductAsAllSawLineIntake', () =>
        sameProductAsAllSawLineIntake(storageArea),
      );
      const sameBin = lazyCrossArea('lazy: sameProductAsLogSorterBin', () =>
        sameProductAsLogSorterBin(storageArea),
      );
      const existsElsewhere = lazyCrossArea('lazy: productExistsInOtherStorageAreas', () =>
        productExistsInOtherStorageAreas(storageArea),
      );
      const fillVolume = lazyTimed('lazy: calculateFillLevelVolume', () =>
        calculateFillLevelVolume(storageArea),
      );
      const fillWeight = lazyTimed('lazy: calculateFillLevelWeight', () =>
        calculateFillLevelWeight(storageArea),
      );
      const sawLineIntakeFunc = crossAreaFunc(
        'lazy: sameProductAsSawLineIntakeFunc',
        sameProductAsSawLineIntake(storageArea),
      );

      storageAreaContext = {
        name: storageArea.name,
        status: storageArea.status,
        type: storageArea.type,
        allowedToPlace,
        empty: products.length === 0,
        id: storageArea.id,
        note: storageArea.note ?? '',
        importantNote: storageArea.importantNote,
        sameProductAsSawLineIntakeFunc: sawLineIntakeFunc,
        get sameProductsAsVehicle() {
          return sameAsVehicle();
        },
        get sameAllowedProductsAsVehicle() {
          return sameAllowedAsVehicle();
        },
        get sameProductAsSawLineIntake() {
          return sameSawAll();
        },
        get sameProductAsLogSorterBin() {
          return sameBin();
        },
        get productExistsInOtherStorageAreas() {
          return existsElsewhere();
        },
        // Deprecated by fillLevelVolume, remove when all customers have changed to fillLevelVolume.
        get fillLevel() {
          return fillVolume();
        },
        get fillLevelVolume() {
          return fillVolume();
        },
        get fillLevelWeight() {
          return fillWeight();
        },
      };

      // ---- Product and product group context ----
      const firstProduct = products[0];

      if (firstProduct) {
        const productGroup = productGroupStore.getById(firstProduct.productGroupId);
        const color = firstProduct.color ?? productGroup?.color ?? null;

        productContext = {
          id: firstProduct.id,
          color: color,
          get name() {
            return displayName();
          },
          multiple: productIds.size > 1,
        };

        if (productGroup) {
          productGroupContext = {
            name: productGroup.name,
            color: productGroup.color,
          };
        }
      }

      // ---- Bulk storage context ----
      if (bulk) {
        const allowedNames = lazy(() => sortedProductNames(bulk.allowedProducts));

        bulkStorageContext = {
          allowMultipleProducts: bulk.allowMultipleProducts,
          disableAutomaticProductChange: bulk.disableAutomaticProductChange,
          get allowedProducts() {
            return allowedNames();
          },
          get content() {
            return productNames();
          },
          get firstMoveToDate() {
            return languageStore.formatMonthDay(bulk.firstMoveToUtc);
          },
          get lastEmptyDate() {
            return languageStore.formatMonthDay(bulk.lastEmptyUtc);
          },
          get lastInventoryDate() {
            return languageStore.formatMonthDay(bulk.lastInventoryUtc);
          },
          get firstMoveToDateTime() {
            return languageStore.formatDateTime(bulk.firstMoveToUtc);
          },
          get lastEmptyDateTime() {
            return languageStore.formatDateTime(bulk.lastEmptyUtc);
          },
          get lastInventoryDateTime() {
            return languageStore.formatDateTime(bulk.lastInventoryUtc);
          },
          get daysSinceFirstMove() {
            return getDaysSinceDate(bulk.firstMoveToUtc);
          },
        };
      }

      // ---- Log yard context ----
      if (logYard) {
        const allowedNames = lazy(() => sortedProductNames(logYard.allowedProducts));
        const binNames = lazyCrossArea('lazy: getLogSorterBinForProducts', () =>
          getLogSorterBinForProducts(storageArea),
        );
        const productionOrderFunc = crossAreaFunc(
          'lazy: sameProductAsLogSorterProductionOrderFunc',
          sameProductAsLogSorterProductionOrder(storageArea),
        );

        logYardContext = {
          allowMultipleProducts: logYard.allowMultipleProducts,
          disableAutomaticProductChange: logYard.disableAutomaticProductChange,
          get allowedProducts() {
            return allowedNames();
          },
          get content() {
            return productNames();
          },
          get firstMoveToDate() {
            return languageStore.formatMonthDay(logYard.firstMoveToUtc);
          },
          get lastEmptyDate() {
            return languageStore.formatMonthDay(logYard.lastEmptyUtc);
          },
          get lastInventoryDate() {
            return languageStore.formatMonthDay(logYard.lastInventoryUtc);
          },
          get firstMoveToDateTime() {
            return languageStore.formatDateTime(logYard.firstMoveToUtc);
          },
          get lastEmptyDateTime() {
            return languageStore.formatDateTime(logYard.lastEmptyUtc);
          },
          get lastInventoryDateTime() {
            return languageStore.formatDateTime(logYard.lastInventoryUtc);
          },
          get daysSinceFirstMove() {
            return getDaysSinceDate(logYard.firstMoveToUtc);
          },
          get logSorterBin() {
            return binNames();
          },
          sameProductAsLogSorterProductionOrderFunc: productionOrderFunc,
        };
      }
    }

    return {
      vehicle: {
        empty:
          (vehicleLogYardContent === null || vehicleLogYardContent.length === 0) &&
          (vehicleBulkContent === null || vehicleBulkContent.length === 0),
      },
      storageArea: storageAreaContext,
      product: productContext,
      storageAreaType: storageAreaTypes,
      storageAreaStatus: storageAreaStatuses,
      inside: storageArea?.id === insideStorageAreaId,
      selected: selected,
      logYard: logYardContext,
      bulk: bulkStorageContext,
      productGroup: productGroupContext,
      color: shape.color,
    };
  }

  function evaluateShapeStyle(
    shape: Shape,
    storageArea: StorageArea | undefined,
    selected: boolean,
    shapeStyle: ReturnType<typeof getShapeStyle>,
  ): ShapeStyleResult {
    const result: ShapeStyleResult = {
      color: '#fafafa',
      outlineColor: '#000000',
      outlineSize: 1,
      label: '',
      opacity: DEFAULT_OPACITY,
      fontSize: 12,
      messageIconVisible: false,
    };

    if (storageArea && searchActive && !searchedStorageAreaIds.has(storageArea.id)) {
      result.opacity = DIMMED_OPACITY;
    }

    if (!shapeStyle) return result;

    let t0 = tick(framePerf);
    const context = buildShapeContext(shape, storageArea, selected);
    tock(framePerf, 'shape: build context', t0);

    t0 = tick(framePerf);
    result.color = shapeStyle.color(context);
    result.outlineColor = shapeStyle.outlineColor(context);
    result.outlineSize = shapeStyle.outlineSize(context);
    result.label = shapeStyle.label(context);
    result.fontSize = shapeStyle.fontSize(context);
    result.messageIconVisible = shapeStyle.messageIconVisible(context);
    tock(framePerf, 'shape: evaluate map style (includes lazy lookups)', t0);

    return result;
  }

  // =========================================================================
  // Map attachment and rendering
  // =========================================================================

  function attach(map: OLMap) {
    const { source, layers } = createLayer(map);

    const raf = useRafFn(() => {
      // Wait with a full render if a full render happen the last 32 milliseconds.
      // Gives better performance if we receive alot fo updates in a short amount of time.
      if (fullRenderReason !== null && performance.now() - fullRenderRequestedAt >= 32) {
        markAllDirty(fullRenderReason);
        fullRenderReason = null;
      }

      if (dirtyViewModels.size === 0) return;
      renderDirtyViewModels(source);
    });

    const stopStyleWatch = watch(selectedStyle, () => {
      crossAreaUsed = false;
      crossAreaScanComplete = false;
      scheduleRenderAll('map style changed');
    });

    const clickKey = map.on('click', (ev) => {
      const t0 = performance.now();

      const finish = (outcome: string) => {
        if (isDebugEnabled(PERF_DEBUG_FLAG)) {
          console.debug(
            `[shapeLayer] Click handled (${outcome}): ${(performance.now() - t0).toFixed(2)} ms`,
          );
        }
      };

      if (ev.originalEvent.altKey) return;
      if (ev.defaultPrevented) return;

      const features = source.getFeaturesAtCoordinate(ev.coordinate);
      if (features.length === 0) {
        map.getAllLayers().forEach((layer) => {
          if (
            layer.get('name') === 'previewRectangleLayer' ||
            layer.get('name') === 'previewPolygonLayer'
          ) {
            const layerSource = layer.getSource() as VectorSource<Feature<Geometry>>;
            const previewFeatures = layerSource?.getFeaturesAtCoordinate(ev.coordinate);
            if (previewFeatures && previewFeatures.length > 0) {
              features.push(...previewFeatures);
            }
          }
        });
      }

      features.sort((a, b) => {
        return (b.get('zIndex') ?? 0) - (a.get('zIndex') ?? 0);
      });

      for (const feature of features) {
        const shapeId = feature.getId();
        if (shapeId === undefined) continue;
        if (typeof shapeId === 'number') continue;
        const viewModel = viewModels.get(shapeId);
        if (!viewModel) continue;

        onClickCallback({
          shapeId,
          storageAreaId: viewModel.shape.storageAreaId,
          multiple: ev.originalEvent.ctrlKey || ev.originalEvent.metaKey,
        });

        finish(`shape ${shapeId}, ${features.length} candidates`);
        return;
      }

      onClickCallback({
        shapeId: null,
        storageAreaId: null,
        multiple: ev.originalEvent.ctrlKey || ev.originalEvent.metaKey,
      });

      finish('no shape');
    });

    unsubscribers.push(() => {
      raf.pause();
      stopStyleWatch();
      unByKey(clickKey);
      for (const layer of layers) map.removeLayer(layer);
      source.clear();
    });

    return source;

    /**
     * Renders the dirty view models. Stops after RENDER_BUDGET_MS and continues in the next frame,
     * so a full refresh of thousands of shapes does not freeze the UI.
     */
    function renderDirtyViewModels(source: VectorSource<Feature<Geometry>>) {
      const perf = isDebugEnabled(PERF_DEBUG_FLAG) ? new PerfStats() : null;
      const frameStart = performance.now();

      framePerf = perf;

      if (!batchInProgress) {
        lookups = createLookupCache();
        batchInProgress = true;
      }

      const frame: FrameChanges = { additions: [], propertiesChanged: false };
      const shapeStyle = getShapeStyle();
      const queuedAtStart = dirtyViewModels.size;
      let processed = 0;

      try {
        const loopStart = tick(perf);

        for (const viewModel of dirtyViewModels) {
          dirtyViewModels.delete(viewModel);

          try {
            renderShape(source, viewModel, shapeStyle, frame);
          } catch (error) {
            console.error(`[shapeLayer] Failed to render shape ${viewModel.id}`, error);
          }

          processed++;

          if (
            processed % BUDGET_CHECK_INTERVAL === 0 &&
            performance.now() - frameStart > RENDER_BUDGET_MS
          ) {
            break;
          }
        }

        tock(perf, 'frame: render shapes loop', loopStart);

        if (frame.additions.length > 0) {
          const t0 = tick(perf);
          source.addFeatures(frame.additions);
          tock(perf, 'source: addFeatures (batch)', t0);
          perf?.count('features added', frame.additions.length);
        }

        if (frame.propertiesChanged) batchPropertiesChanged = true;
      } finally {
        framePerf = null;

        if (dirtyViewModels.size === 0) {
          // Batch finished, every dirty view model has been rendered with the current style.

          if (batchPropertiesChanged) {
            const t0 = tick(perf);
            source.changed();
            tock(perf, 'source: changed() (end of batch)', t0);
            batchPropertiesChanged = false;
          }

          crossAreaScanComplete = true;
          batchInProgress = false;
          lookups = createLookupCache();
        }
      }

      if (perf) {
        perf.count('view models queued at frame start', queuedAtStart);
        perf.count('view models rendered', processed);
        perf.count('view models deferred to next frame', dirtyViewModels.size);
        perf.report(
          `render frame (${processed}/${queuedAtStart} view models)`,
          performance.now() - frameStart,
        );
      }
    }

    function removeFeature(source: VectorSource<Feature<Geometry>>, viewModel: ShapeViewModel) {
      if (!viewModel.feature) return;

      const t0 = tick(framePerf);
      source.removeFeature(viewModel.feature);
      viewModel.feature = null;
      tock(framePerf, 'source: removeFeature', t0);
      framePerf?.count('features removed');
    }

    function renderShape(
      source: VectorSource<Feature<Geometry>>,
      viewModel: ShapeViewModel,
      shapeStyle: ReturnType<typeof getShapeStyle>,
      frame: FrameChanges,
    ) {
      const perf = framePerf;
      let t0 = tick(perf);

      const storageAreaId = viewModel.shape.storageAreaId;

      if (
        viewModel.deleteScheduled ||
        hiddenShapeIds.has(viewModel.id) ||
        (storageAreaId !== null && hiddenStorageAreaIds.has(storageAreaId))
      ) {
        removeFeature(source, viewModel);

        if (viewModel.deleteScheduled) {
          viewModels.delete(viewModel.id);
        }

        tock(perf, 'shape: hidden / deleted', t0);
        return;
      }

      const shape = viewModel.shape;
      const shapeFeature = shape.shapeGeoJson;

      if (!shapeFeature?.geometry) {
        removeFeature(source, viewModel);
        return;
      }

      const shapeGeometry = shapeFeature.geometry;

      let storageArea: StorageArea | undefined;
      let selected = false;

      if (storageAreaId) {
        storageArea = storageAreaStore.getById(storageAreaId);
        selected = selectedStorageAreaIds.has(storageAreaId);
      }

      const style = evaluateShapeStyle(shape, storageArea, selected, shapeStyle);

      // ---- Feature creation / geometry ----
      let feature = viewModel.feature;
      let isNew = false;

      if (!feature) {
        t0 = tick(perf);
        shapeFeature.id = shape.id;
        setShapeId(shapeFeature, shape.id);
        feature = geojson.readFeature(shapeFeature) as Feature<Geometry>;
        viewModel.feature = feature;
        viewModel.geometryDirty = false;
        isNew = true;
        tock(perf, 'shape: create feature (GeoJSON read)', t0);
        perf?.count('geometries created');
      } else if (viewModel.geometryDirty) {
        t0 = tick(perf);
        feature.setGeometry(geojson.readGeometry(shapeGeometry));
        viewModel.geometryDirty = false;
        tock(perf, 'shape: update geometry', t0);
        perf?.count('geometries updated');
      }

      // ---- Properties ----
      t0 = tick(perf);
      const propertiesChanged = applyProperties(feature, {
        label: style.label,
        color: style.color,
        outlineColor: style.outlineColor,
        outlineSize: style.outlineSize,
        opacity: style.opacity,
        fontSize: style.fontSize,
        textColor: calculateTextColor(style.color),
        messageIconVisible: style.messageIconVisible,
        zIndex: shape.zIndex,
      });
      tock(perf, 'shape: update properties', t0);

      if (isNew) {
        frame.additions.push(feature);
      } else if (propertiesChanged) {
        frame.propertiesChanged = true;
        perf?.count('property updates');
      } else {
        perf?.count('no visual change (skipped)');
      }
    }
  }
}

// ---------------------------------------------------------------------------
// OpenLayers layers
// ---------------------------------------------------------------------------

function createLayer(map: OLMap) {
  const source = new VectorSource();

  const polygonLayer = new VectorLayer({
    source: source,
    updateWhileAnimating: true,
    updateWhileInteracting: true,
    style: (feature) => {
      const properties = feature.getProperties();
      const label = properties['label'];
      const color = properties['color'];
      const opacity = properties['opacity'];
      const outlineColor = properties['outlineColor'];
      const outlineSize = properties['outlineSize'];
      const baseFontSize = properties['fontSize'] || 12;
      const textColor = properties['textColor'];
      const mode = properties['mode'];
      const zIndex = properties['zIndex'] || 0;

      if (mode !== 'polygon') return;

      const resolution = map.getView().getResolution() || 1;
      let scaledFontSize = baseFontSize / resolution;

      scaledFontSize = Math.max(baseFontSize, scaledFontSize);

      let bold = false;

      const richText = parseRichText(label);
      const text: string[] = [];

      for (const token of richText.tokens) {
        switch (token.type) {
          case 'bold-start':
            bold = true;
            break;
          case 'bold-end':
            bold = false;
            break;
          case 'text':
            text.push(token.content);
            text.push(`${bold ? 'bold' : ''} ${scaledFontSize}px sans-serif`);
            break;
        }
      }

      return new Style({
        fill: new Fill({
          color: Color.setAlpha(color, opacity),
        }),
        stroke: new Stroke({
          color: Color.setAlpha(outlineColor, opacity),
          width: outlineSize,
        }),
        text: new Text({
          text: text,
          font: `${scaledFontSize}px sans-serif`,
          fill: new Fill({
            color: Color.setAlpha(textColor, opacity),
          }),
        }),
        zIndex: zIndex,
      });
    },
  });

  const rectangleLayer = new VectorLayer({
    source: source,
    style: rectangleStyle,
    updateWhileAnimating: true,
    updateWhileInteracting: true,
  });

  rectangleLayer.setRenderOrder((a, b) => {
    return (a.get('zIndex') ?? 0) - (b.get('zIndex') ?? 0);
  });

  map.addLayer(polygonLayer);
  map.addLayer(rectangleLayer);

  return {
    source,
    layers: [polygonLayer, rectangleLayer],
  };
}
